import * as React from 'react';
import { Dialog, DialogContent, DialogContentText, DialogTitle, useMediaQuery, Typography, Divider, Box, Button, IconButton, Grid, TextField, CardMedia, CircularProgress, Snackbar, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
// ✅ Este archivo vive en src/components/activities/, por eso son dos niveles
// hasta src/services/motorService.js (a diferencia del "../services/..." que
// usa MainComponent, que está un nivel más arriba, en src/components/).
import { obtenerDetalleActividad, grabarCarrito, obtenerMuestroCarrito } from '../../services/motorService';
import useSession from '../../services/session';
// ✅ Mismo contexto de carrito que usa RoomTypeRatePlan.js (src/components/roomType/),
// porque el carrito es UNO SOLO: las actividades se agregan como líneas de ese
// mismo carrito, no en uno aparte.
import RoomTypeRatePlanContext from '../contexts/RoomTypeRatePlanContext';

// ✅ El formato real (png/jpeg/etc) puede variar, así que lo detectamos
// mirando el "magic number" de los primeros bytes del base64 en vez de
// asumir uno fijo (mismo criterio que en Activities.js).
const detectImageMime = (base64) => {
  if (base64.startsWith('iVBORw0KGgo')) return 'image/png';
  if (base64.startsWith('/9j/')) return 'image/jpeg';
  if (base64.startsWith('R0lGODlh') || base64.startsWith('R0lGODdh')) return 'image/gif';
  if (base64.startsWith('UklGR')) return 'image/webp';
  return 'image/jpeg'; // fallback razonable si no matchea ninguna firma conocida
};

// ✅ "hotprodfoto" viene en base64 "pelado" (sin el prefijo
// "data:image/...;base64,"), igual que HotProdFoto en el listado de
// Activities.js. Si en algún caso ya viniera con el prefijo puesto, lo
// dejamos tal cual.
const buildImageSrc = (base64) => {
  if (!base64) return null;
  if (/^data:image\//i.test(base64)) return base64;
  const clean = base64.replace(/\s/g, '');
  return `data:${detectImageMime(clean)};base64,${clean}`;
};

const formatearMonto = (valor) => {
  const n = Number(valor) || 0;
  return n.toLocaleString('es-CL', { minimumFractionDigits: 0 });
};

// ✅ El precio de la API es POR PERSONA. Si hay precio diferenciado (adulto
// y/o menor distinto de 0), se usa ese valor para cada tipo de pax. Si no hay
// diferenciado (adulto y menor en 0), el precio de "general" aplica por igual
// a adultos y menores.
const getUnitPrices = (precio) => {
  if (!precio) return null;
  const adulto = Number(precio.adulto) || 0;
  const menor = Number(precio.menor) || 0;
  const general = Number(precio.general) || 0;
  if (adulto > 0 || menor > 0) {
    return { adulto, menor };
  }
  return { adulto: general, menor: general };
};

// ⚠️ "hotcod" acá debe ser el código de hotel de ADMINTOUR (MotorHotAdmintour,
// obtenido en Activities.js vía Muestro_DatosHotel), NO el código de hotel
// del motor que viene por la URL. La API Actividad_Detalle (obtenerDetalleActividad)
// se identifica con el hotel usando el código de Admintour.
export default function DialogMoreInfoActivity({ open, handleClose, activity, hotcod }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // ⚠️ OJO: "hotcod" (prop) es el código ADMINTOUR del hotel (ver comentario más
  // abajo, usado para Actividad_Detalle). El carrito (grabarCarrito /
  // obtenerMuestroCarrito) en cambio se identifica con el código de hotel DEL
  // MOTOR, que es el que viaja por la URL ("?hotcod=") o en la cookie "hotCod"
  // — mismo criterio que usa RoomTypeRatePlan.js. Son dos códigos distintos
  // aunque se llamen parecido, por eso no reusamos la prop "hotcod" para esto.
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const motorHotCod = params.get('hotcod') ?? Cookies.get('hotCod') ?? null;
  const sesion = useSession();
  const { setCartItems } = React.useContext(RoomTypeRatePlanContext);

  const [selectedDate, setSelectedDate] = React.useState(dayjs());
  // ✅ Ahora guardamos el horario completo (no solo el string de la hora),
  // porque la API devuelve Linea/Nombre/Hora/Cupos por cada horario y los
  // necesitamos para mostrar el cupo real y habilitar/deshabilitar "Comprar".
  const [selectedHorario, setSelectedHorario] = React.useState(null);
  const [selectedAdults, setSelectedAdults] = React.useState(1);
  const [selectedChildren, setSelectedChildren] = React.useState(0);
  // ✅ Aviso que se muestra cuando el usuario intenta cargar más gente de la
  // que hay cupo disponible para el horario elegido.
  const [cupoWarning, setCupoWarning] = React.useState(false);

  // ✅ Estado del "Comprar" -> agregar al carrito.
  const [addingToCart, setAddingToCart] = React.useState(false);

  // ✅ Feedback de error visible (antes solo quedaba en consola: el usuario
  // no se enteraba de nada si grabarCarrito o obtenerMuestroCarrito fallaban).
  const [addToCartError, setAddToCartError] = React.useState(null);

  // ✅ Detalle real de la actividad para la fecha elegida (itinerario, foto,
  // precio y horarios con cupos), traído de API_MotorActividades/Actividad_Detalle.
  const [detalle, setDetalle] = React.useState(null);
  const [loadingDetalle, setLoadingDetalle] = React.useState(false);
  const [errorDetalle, setErrorDetalle] = React.useState(null);

  // ✅ Si la imagen (la del detalle o la de respaldo del listado) falla al
  // cargar, mostramos un placeholder en vez de ocultar la foto en silencio.
  const [imageLoadFailed, setImageLoadFailed] = React.useState(false);

  // ✅ Al abrir el dialog (o cambiar de actividad) arrancamos limpio: fecha de
  // hoy, sin hora elegida y sin cupos/pax cargados de una consulta anterior.
  React.useEffect(() => {
    if (!open) return;
    setSelectedDate(dayjs());
    setSelectedHorario(null);
    setSelectedAdults(1);
    setSelectedChildren(0);
    setDetalle(null);
    setErrorDetalle(null);
    setAddToCartError(null);
    setImageLoadFailed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activity?.hotprodcod]);

  // ✅ Cada vez que cambia la fecha elegida (o se abre el dialog), buscamos el
  // detalle de la actividad para ESA fecha puntual: trae los horarios y cupos
  // reales de ese día.
  React.useEffect(() => {
    // 🐞 DEBUG: para ver si el effect se está ejecutando y con qué datos.
    console.log('[DialogMoreInfoActivity] effect de detalle', {
      open,
      hotcod,
      hotprodcod: activity?.hotprodcod,
      selectedDate: selectedDate ? selectedDate.format('YYYY-MM-DD') : selectedDate,
    });

    if (!open || !hotcod || !activity?.hotprodcod || !selectedDate) {
      // 🐞 DEBUG: si ves este log, el llamado NO se hace porque falta alguno
      // de estos 4 datos (mirá cuál viene undefined/null/false arriba).
      console.log('[DialogMoreInfoActivity] se salta el llamado a la API, falta algún dato', {
        open,
        hotcod,
        hotprodcod: activity?.hotprodcod,
        selectedDate,
      });
      return;
    }

    let cancelado = false;
    setLoadingDetalle(true);
    setErrorDetalle(null);

    const fecha = selectedDate.format('YYYY-MM-DD');
    console.log('[DialogMoreInfoActivity] llamando a obtenerDetalleActividad', {
      hotcod,
      hotprodcod: activity.hotprodcod,
      fecha,
    });

    obtenerDetalleActividad(hotcod, activity.hotprodcod, fecha)
      .then((data) => {
        console.log('[DialogMoreInfoActivity] respuesta de obtenerDetalleActividad', data);
        if (cancelado) return;
        if (data?.ErrorActividadDetalle && data.ErrorActividadDetalle !== 'Actividad encontrada') {
          console.log('[DialogMoreInfoActivity] la API respondió con error de negocio', data.ErrorActividadDetalle);
          setErrorDetalle(data.ErrorActividadDetalle);
          setDetalle(null);
          return;
        }
        setDetalle(data);
        setImageLoadFailed(false);
      })
      .catch((err) => {
        console.log('[DialogMoreInfoActivity] error llamando a obtenerDetalleActividad', err);
        if (!cancelado) {
          setErrorDetalle('No se pudo obtener la disponibilidad de la actividad para esta fecha.');
          setDetalle(null);
        }
      })
      .finally(() => {
        if (!cancelado) setLoadingDetalle(false);
      });

    return () => {
      cancelado = true;
    };
  }, [open, hotcod, activity?.hotprodcod, selectedDate]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedHorario(null); // Reset horario elegido al cambiar de fecha
  };

  const handleHourSelect = (horario) => {
    setSelectedHorario(horario);
    setCupoWarning(false);
  };

  const horariosDelDia = detalle?.Dia?.Horarios || [];
  // null = todavía no se eligió horario.
  const cupoDisponible = selectedHorario ? Number(selectedHorario.Cupos) : null;
  const totalPax = selectedAdults + selectedChildren;

  // ✅ Si cambia el horario (y por lo tanto el cupo disponible) y la cantidad de
  // gente ya cargada supera el nuevo cupo, la ajustamos para que nunca quede por
  // encima de lo disponible.
  React.useEffect(() => {
    if (cupoDisponible == null) return;
    setSelectedAdults((prevAdults) => Math.min(prevAdults, cupoDisponible));
    setSelectedChildren((prevChildren) => {
      const maxChildren = Math.max(0, cupoDisponible - Math.min(selectedAdults, cupoDisponible));
      return Math.min(prevChildren, maxChildren);
    });
    setCupoWarning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cupoDisponible]);

  // ✅ Si lo que pide el usuario supera el cupo disponible, lo dejamos en el
  // máximo posible y mostramos el aviso (en vez de solo recortar en silencio).
  const handleAdultsChange = (value) => {
    const raw = Math.max(0, value);
    if (cupoDisponible != null) {
      const maxAllowed = Math.max(0, cupoDisponible - selectedChildren);
      if (raw > maxAllowed) {
        setCupoWarning(true);
        setSelectedAdults(maxAllowed);
        return;
      }
    }
    setCupoWarning(false);
    setSelectedAdults(raw);
  };

  const handleChildrenChange = (value) => {
    const raw = Math.max(0, value);
    if (cupoDisponible != null) {
      const maxAllowed = Math.max(0, cupoDisponible - selectedAdults);
      if (raw > maxAllowed) {
        setCupoWarning(true);
        setSelectedChildren(maxAllowed);
        return;
      }
    }
    setCupoWarning(false);
    setSelectedChildren(raw);
  };

  // ✅ Precio total para la cantidad de personas seleccionada (el precio de la
  // API es por persona). Preferimos el precio real del detalle; si todavía no
  // llegó (o falló la consulta), mostramos el precio del listado como fallback.
  const unitPrices = getUnitPrices(detalle?.Precio);
  const totalPersonas = selectedAdults + selectedChildren;
  const totalPrice = unitPrices
    ? unitPrices.adulto * selectedAdults + unitPrices.menor * selectedChildren
    : null;

  let priceLabel;
  if (unitPrices && totalPersonas > 0) {
    priceLabel = `$${formatearMonto(totalPrice)} (precio total para ${totalPersonas} persona${totalPersonas === 1 ? '' : 's'}: ${selectedAdults} adulto(s), ${selectedChildren} menor(es))`;
  } else if (unitPrices) {
    priceLabel = 'Elegí la cantidad de personas para ver el precio total.';
  } else {
    priceLabel = activity?.price ? `${activity?.currency} ${activity?.price}` : 'Consultar';
  }

  const nombreActividad = detalle?.hotprodnomlargo || activity?.activityName;
  // ✅ Preferimos la foto real del detalle (base64); si todavía no cargó o no
  // vino, caemos a la que ya traía el listado (que puede ser la genérica).
  const imagenActividad = buildImageSrc(detalle?.hotprodfoto) || activity?.image;

  const sinCupo = cupoDisponible === 0;
  const noSePuedeComprar = !selectedHorario || sinCupo || totalPax === 0;

  // ✅ Agregar la actividad al carrito. Es el MISMO carrito que usan las
  // habitaciones (grabarCarrito / obtenerMuestroCarrito de motorService), pero
  // para una actividad:
  //   - "TipoHab" va vacío (no aplica, no es una habitación).
  //   - "tarifa" va con el CÓDIGO DEL PRODUCTO de la actividad (hotprodcod),
  //     no con un TarifaCodAdm como en las habitaciones.
  const handleAgregarAlCarrito = async () => {
    if (noSePuedeComprar || addingToCart) return;

    setAddingToCart(true);
    setAddToCartError(null);
    try {
      const fecha = selectedDate.format('YYYY-MM-DD');
      const fechaHasta = selectedDate.clone().add(1, 'day').format('YYYY-MM-DD');

      const carritoActividad = {
        hotelcarrito: motorHotCod,
        Sesion: sesion,
        DesdeFecha: fecha,
        hastafecha: fechaHasta,
        mayores: selectedAdults,
        menores: selectedChildren,
        TipoHab: '',
        tarifa: parseInt(activity?.hotprodcod, 10),
      };

      console.log('[DialogMoreInfoActivity] grabando actividad en el carrito', carritoActividad);
      await grabarCarrito(carritoActividad);

      const updatedCart = await obtenerMuestroCarrito(motorHotCod, sesion);
      console.log('[DialogMoreInfoActivity] carrito actualizado luego de agregar actividad', updatedCart);
      // ✅ cartItems es directamente el array del carrito (misma forma que usan
      // Checkout.js, ReservationSummary.js y Summary.js). Antes se guardaba acá
      // como { cartNoPrice, price }, una forma distinta a la que usa el resto
      // de la app, y esa inconsistencia era la causa del "no se actualiza el
      // carrito" / "cartItems.reduce is not a function".
      setCartItems(updatedCart || []);

      console.log('[DialogMoreInfoActivity] actividad agregada al carrito OK', nombreActividad);

      // ✅ FIX: antes esta función nunca cerraba el dialog al agregar con éxito
      // (solo apagaba el spinner en el finally), así que el usuario tenía que
      // cerrarlo él mismo a mano. Ahora, si todo salió bien, cerramos el
      // dialog acá mismo.
      handleClose();
    } catch (err) {
      console.error('[DialogMoreInfoActivity] error agregando actividad al carrito', err);
      // ✅ Si falla, dejamos el dialog abierto (para que el usuario pueda
      // reintentar) y le mostramos el error en vez de fallar en silencio.
      setAddToCartError('No se pudo agregar la actividad al carrito. Intentá nuevamente.');
    } finally {
      setAddingToCart(false);
    }
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
    >
      <DialogTitle id="responsive-dialog-title">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {nombreActividad}
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* ✅ Una única foto, sin carrusel. Si no hay imagen (ni la del
            detalle ni la de respaldo del listado) o falla al cargar,
            mostramos un placeholder en vez de dejar el hueco vacío. */}
        {imagenActividad && !imageLoadFailed ? (
          <CardMedia
            component="img"
            image={imagenActividad}
            alt={nombreActividad}
            sx={{ width: '100%', maxHeight: 320, objectFit: 'cover', borderRadius: 2 }}
            onLoad={(e) => {
              // 🐞 DEBUG: mismo chequeo que en Activities.js — si "real" es
              // bastante más chico que "mostrada", la foto de origen es de
              // baja resolución y por eso se ve pixelada al estirarla.
              console.log('[DialogMoreInfoActivity] tamaño de imagen', nombreActividad, {
                real: `${e.target.naturalWidth}x${e.target.naturalHeight}`,
                mostrada: `${e.target.clientWidth}x${e.target.clientHeight}`,
              });
            }}
            onError={() => {
              // Si la foto vino corrupta/inválida, mostramos el placeholder.
              setImageLoadFailed(true);
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 220,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              bgcolor: 'grey.200',
              color: 'grey.500',
              borderRadius: 2,
            }}
          >
            <ImageNotSupportedOutlinedIcon sx={{ fontSize: 48 }} />
            <Typography variant="caption" color="inherit">
              Sin imagen disponible
            </Typography>
          </Box>
        )}
        <DialogContent>
          <Typography variant="h6" component="div">
            Detalle de la actividad
          </Typography>
          {detalle?.hotproditinerario ? (
            // ✅ El backend devuelve el itinerario como HTML (<div>, &nbsp;, etc.)
            <DialogContentText component="div" sx={{ whiteSpace: 'normal' }}>
              <Box dangerouslySetInnerHTML={{ __html: detalle.hotproditinerario }} />
            </DialogContentText>
          ) : (
            <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
              {activity?.description}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogContent>
          <Typography variant="h6" component="div">
            Selección de fecha y hora para la actividad
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              label="¿Cuándo voy?"
              minDate={dayjs()}
            />
          </LocalizationProvider>

          <Typography variant="h6" component="div" sx={{ mt: 2 }}>
            Horas disponibles:
          </Typography>

          {loadingDetalle && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Buscando disponibilidad...
              </Typography>
            </Box>
          )}

          {!loadingDetalle && errorDetalle && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {errorDetalle}
            </Typography>
          )}

          {!loadingDetalle && !errorDetalle && (
            horariosDelDia.length > 0 ? (
              horariosDelDia.map((horario) => (
                <Button
                  key={horario.Linea}
                  variant={selectedHorario?.Linea === horario.Linea ? 'contained' : 'outlined'}
                  sx={{ m: 1 }}
                  disabled={Number(horario.Cupos) === 0}
                  onClick={() => handleHourSelect(horario)}
                >
                  {horario.Hora}
                </Button>
              ))
            ) : (
              <Typography variant="body1" color="text.secondary">
                No hay horas disponibles para el día seleccionado.
              </Typography>
            )
          )}

          {/* ✅ El cupo solo se muestra cuando queda poco (menos de 5), para no
              saturar la pantalla cuando hay disponibilidad de sobra. */}
          {selectedHorario && cupoDisponible != null && cupoDisponible < 5 && (
            <Typography
              variant="body2"
              sx={{ mt: 1, fontWeight: 600, color: sinCupo ? 'error.main' : 'warning.main' }}
            >
              {sinCupo
                ? 'No quedan cupos disponibles para este horario.'
                : `Solo quedan ${cupoDisponible} cupo(s) para este día.`}
            </Typography>
          )}

          <Grid container spacing={2} sx={{ mt: 1.5 }}>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                Adultos
              </Typography>
              <TextField
                fullWidth
                type="number"
                // ✅ A propósito SIN "max" acá: si el input nativo tiene max, el
                // navegador frena el spinner sin disparar onChange al llegar al
                // tope, y entonces nuestro aviso de cupo nunca llega a mostrarse.
                // Dejamos que sea handleAdultsChange el único que clampea y avisa.
                inputProps={{ min: 0 }}
                value={selectedAdults}
                onChange={(e) => handleAdultsChange(Number(e.target.value) || 0)}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                Menores
              </Typography>
              <TextField
                fullWidth
                type="number"
                // ✅ Mismo motivo que en Adultos: sin "max" nativo, así el aviso
                // de cupo se dispara también con las flechitas del input.
                inputProps={{ min: 0 }}
                value={selectedChildren}
                onChange={(e) => handleChildrenChange(Number(e.target.value) || 0)}
              />
            </Grid>
            {cupoWarning && cupoDisponible != null && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                  No puedes agregar más personas a esta actividad porque para este día y
                  horario tenemos un cupo de {cupoDisponible} persona{cupoDisponible === 1 ? '' : 's'} disponible.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <Divider />
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
              <DialogContentText variant="h6" component="div">
                {unitPrices && totalPersonas > 0 ? `Precio: ${priceLabel}` : priceLabel}
              </DialogContentText>
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                disabled={noSePuedeComprar || addingToCart}
                onClick={handleAgregarAlCarrito}
                startIcon={addingToCart ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {addingToCart ? 'Agregando...' : 'Agregar al carrito'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </DialogContent>

      {/* ✅ Feedback visible si falla el agregado (antes solo quedaba en consola). */}
      <Snackbar
        open={!!addToCartError}
        autoHideDuration={5000}
        onClose={() => setAddToCartError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setAddToCartError(null)} severity="error" sx={{ width: '100%' }}>
          {addToCartError}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}